import {FilePicker, PickFilesResult} from '@capawesome/capacitor-file-picker'
import {Folder, PhotoLibrary, VideoLibrary} from '@mui/icons-material'
import {
  Button,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {FC, MutableRefObject, useCallback, useMemo} from 'react'
import {useNotify, useTranslate} from 'react-admin'

import {FileSelectionValidator} from '../hooks/useFileSelection'
import {arrayBufferToHex, base64ToArrayBuffer} from '../utils/buffers'
import {isNativeIosApp} from '../utils/platform'

const MobileFilePickerPopper: FC<MobileFilePickerPopperProps> = ({
  anchorElementRef,
  encoding,
  isOpen = false,
  mimeTypes,
  multiple = false,
  onClose,
  onSelectFiles,
  onValidateFile,
}) => {
  const styles = useStyles()
  const notify = useNotify()
  const translate = useTranslate()
  const fileMimeTypes = useMemo(
    () => Array.isArray(mimeTypes) ? mimeTypes : mimeTypes.split(','),
    [mimeTypes],
  )
  const onPickFiles = useCallback(
    ({files}: PickFilesResult) => {
      const fileAttachments = files.reduce(
        (attachments: FileAttachment[], pickedFile) => {
          const arrayBuffer = base64ToArrayBuffer(pickedFile.data as string)
          const file = new File(
            [arrayBuffer],
            pickedFile.name,
            {type: pickedFile.mimeType},
          )
          const validationError = onValidateFile(file)
          if (validationError) {
            notify(validationError.message, {type: 'error'})
            return attachments
          }
          const attachment = {
            encodedContent: encoding === 'hex' ?
              arrayBufferToHex(arrayBuffer) : (
                encoding === 'base64' ? pickedFile.data : undefined
              ),
            file,
            filename: file.name,
            mimeType: file.type,
          } as FileAttachment
          return [...attachments, attachment]
        },
        [],
      )
      onSelectFiles(fileAttachments)
    },
    [encoding, onSelectFiles, notify, onValidateFile]
  )
  return (
    <Popper
      anchorEl={anchorElementRef.current}
      modifiers={[
        {enabled: true, name: 'flip'},
        {name: 'offset', options: {offset: [16, -40]}},
      ]}
      open={isOpen}
      placement="bottom-start"
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper className={styles.root} elevation={20} onClick={e => e.stopPropagation()}>
          <Button
            className={styles.option}
            onClick={e => {
              e.stopPropagation()
              onClose()
              FilePicker.pickFiles({
                limit: (multiple ? 0 : 1),
                readData: true,
                types: fileMimeTypes,
              })
                .then(onPickFiles)
                .catch(error => {
                  if (error.message.includes('pickFiles canceled')) return
                  throw error
                })
            }}
            variant="text"
          >
            <Folder color="action" />
            <Typography>
              {translate('chat.toolbar.fileAttachment.selectFromFiles')}
            </Typography>
          </Button>
          <Button
            className={styles.option}
            onClick={e => {
              e.stopPropagation()
              onClose()
              FilePicker.pickImages({
                limit: (multiple ? 0 : 1),
                readData: true,
                ...(isNativeIosApp && {skipTranscoding: false}),
              })
                .then(onPickFiles)
                .catch(error => {
                  if (error.message.includes('pickFiles canceled')) return
                  throw error
                })
            }}
            variant="text"
          >
            <PhotoLibrary color="action" />
            <Typography>
              {translate('chat.toolbar.fileAttachment.selectImagesFromGallery')}
            </Typography>
          </Button>
          <Button
            className={styles.option}
            onClick={e => {
              e.stopPropagation()
              onClose()
              FilePicker.pickVideos({limit: (multiple ? 0 : 1), readData: true})
                .then(onPickFiles)
                .catch(error => {
                  if (error.message.includes('pickFiles canceled')) return
                  throw error
                })
            }}
            variant="text"
          >
            <VideoLibrary color="action" />
            <Typography>
              {translate('chat.toolbar.fileAttachment.selectVideosFromGallery')}
            </Typography>
          </Button>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

const useStyles = makeStyles(theme => ({
  option: {
    '& .MuiButton-label': {
      gap: theme.remSpacing(1),
      justifyContent: 'start',
    },
    '& .MuiTypography-root': {
      flexGrow: 1,
      textAlign: 'start',
    },
    '&.MuiButton-text': {
      padding: theme.remSpacing(1),
    },
    display: 'flex',
    flexGrow: 1,
    gap: theme.remSpacing(2),
  },
  root: {
    '&.MuiPaper-rounded': {
      borderRadius: theme.remSpacing(2),
    },
    alignItems: 'stretch',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.remSpacing(1),
  },
}))

interface MobileFilePickerPopperProps<
  TEncoding extends FileEncoding | undefined = FileEncoding
> {
  anchorElementRef: MutableRefObject<HTMLDivElement | undefined>,
  encoding?: TEncoding
  isOpen?: boolean
  mimeTypes: string | string[]
  multiple?: boolean
  onClose: () => void
  onSelectFiles: (files: FileAttachment<TEncoding>[]) => void
  onValidateFile: FileSelectionValidator,
}

export default MobileFilePickerPopper
